import {
  getQueryKey,
  useApi,
  useDeleteQuery,
  useGetQuery,
  usePatchQuery,
  usePostQuery,
  useRouteParams
} from "@quortex/front-common"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import type { RichPushEndpoint } from "@/App/play/App/Main/Pool/endpoints/ottEndpoints/OttEndpointList/types"
import type { PoolParams, PushEndpointParams } from "./types"

interface BatchVariables {
  endpoints: Array<RichPushEndpoint>
  enabled: boolean
  headers?: Record<string, string>
}

//--------------------------------------------------------------------------------------------------
// URLs
//--------------------------------------------------------------------------------------------------

/** @returns /pools/${poolUuid}/push_endpoints/?org=${orgUuid} */
export const getPushEndpointListUrl = ({ orgUuid, poolUuid }: Required<PoolParams>) =>
  `/pools/${poolUuid}/push_endpoints/?org=${orgUuid}`

/** @returns /pools/${poolUuid}/push_endpoints/${pushEndpointUuid}/?org=${orgUuid} */
export const getPushEndpointUrl = ({
  orgUuid,
  poolUuid,
  pushEndpointUuid
}: Required<PushEndpointParams>) =>
  `/pools/${poolUuid}/push_endpoints/${pushEndpointUuid}/?org=${orgUuid}`

//--------------------------------------------------------------------------------------------------
// Queries & mutations
//--------------------------------------------------------------------------------------------------

export const usePushEndpointList = (params?: PoolParams) => {
  const mergedParams = useRouteParams(params)
  const listUrl = getPushEndpointListUrl(mergedParams)

  return useGetQuery<api.ott.PaginatedPushEndpointList>("ott", listUrl)
}

export const usePushEndpoint = (params?: PushEndpointParams) => {
  const mergedParams = useRouteParams(params)
  const url = getPushEndpointUrl(mergedParams)

  return useGetQuery<api.ott.PushEndpoint>("ott", url)
}

export const useAddPushEndpoint = (params?: PoolParams) => {
  const mergedParams = useRouteParams(params)
  const listUrl = getPushEndpointListUrl(mergedParams)

  type TRequest = api.ott.PushEndpointRequest
  type TResponse = api.ott.PushEndpoint

  return usePostQuery<TRequest, TResponse>("ott", listUrl, [listUrl])
}

export const useUpdatePushEndpoint = (params?: PushEndpointParams) => {
  const mergedParams = useRouteParams(params)
  const url = getPushEndpointUrl(mergedParams)
  const listUrl = getPushEndpointListUrl(mergedParams)

  type TRequest = DeepPartial<api.ott.PushEndpointRequest>
  type TResponse = api.ott.PushEndpoint

  return usePatchQuery<TRequest, TResponse>("ott", url, [listUrl])
}

export const useDeletePushEndpoint = (params?: PushEndpointParams) => {
  const mergedParams = useRouteParams(params)
  const url = getPushEndpointUrl(mergedParams)
  const listUrl = getPushEndpointListUrl(mergedParams)

  return useDeleteQuery("ott", url, [listUrl])
}

export const useBatchUpdatePushEndpoints = ({
  orgUuid,
  poolUuid
}: {
  orgUuid?: string
  poolUuid?: string
}) => {
  const queryClient = useQueryClient()
  const { jsonPatch } = useApi("ott")

  return useMutation<void, Error, BatchVariables>({
    mutationFn: async ({ endpoints, enabled }) => {
      if (!orgUuid || !poolUuid) {
        console.warn("Skipping Push Endpoints update: missing orgUuid or poolUuid")
        return
      }
      await Promise.all(
        endpoints.map(async endpoint => {
          const params = { orgUuid, poolUuid, pushEndpointUuid: endpoint.uuid }
          const url = getPushEndpointUrl(params)
          await jsonPatch(url, { enabled })
        })
      )
    },
    onError: error => {
      console.error("Batch update Push Endpoints failed:", error)
    },
    onSuccess: async () => {
      if (!orgUuid || !poolUuid) {
        return
      }
      const listUrl = getPushEndpointListUrl({ orgUuid, poolUuid })
      await queryClient.refetchQueries({ queryKey: getQueryKey("ott", listUrl) })
    }
  })
}
